
import { toast } from "@/hooks/use-toast";

export type UploadErrorType = 
  | 'permission_denied'
  | 'storage_limit'
  | 'file_too_large'
  | 'invalid_file_type'
  | 'network_error'
  | 'server_error'
  | 'verification_failed'
  | 'user_not_found'
  | 'email_update_failed'
  | 'deletion_failed'
  | 'unknown';

export class UploadError extends Error {
  type: UploadErrorType;
  details: string;
  recoverable: boolean;

  constructor(
    type: UploadErrorType,
    message: string,
    details: string,
    recoverable: boolean
  ) {
    super(message);
    this.name = 'UploadError';
    this.type = type;
    this.message = message;
    this.details = details;
    this.recoverable = recoverable;
  }
}

export const categorizeUploadError = (error: any): UploadError => {
  const message = error?.message || 'Upload failed';
  
  // Extract detailed message if available
  let details = error?.details || message;
  
  // Default to unknown error type
  let type: UploadErrorType = 'unknown';
  let recoverable = true;
  
  // Categorize based on message content
  if (message.toLowerCase().includes('permission') || message.toLowerCase().includes('denied')) {
    type = 'permission_denied';
    recoverable = false;
  } else if (message.toLowerCase().includes('storage limit')) {
    type = 'storage_limit';
    recoverable = false; 
  } else if (message.toLowerCase().includes('large') || message.toLowerCase().includes('size')) {
    type = 'file_too_large';
    recoverable = true;
  } else if (message.toLowerCase().includes('file type') || message.toLowerCase().includes('format')) {
    type = 'invalid_file_type';
    recoverable = true;
  } else if (message.toLowerCase().includes('network') || message.toLowerCase().includes('connection')) {
    type = 'network_error';
    recoverable = true;
  } else if (message.toLowerCase().includes('server') || message.toLowerCase().includes('unavailable')) {
    type = 'server_error';
    recoverable = true;
  } else if (message.toLowerCase().includes('verif') || message.toLowerCase().includes('image')) {
    type = 'verification_failed';
    recoverable = true;
  } else if (message.toLowerCase().includes('not found') || message.toLowerCase().includes('user')) {
    type = 'user_not_found';
    recoverable = false;
  } else if (message.toLowerCase().includes('email') || message.toLowerCase().includes('update')) {
    type = 'email_update_failed';
    recoverable = true;
  } else if (message.toLowerCase().includes('delete') || message.toLowerCase().includes('remove')) {
    type = 'deletion_failed';
    recoverable = true;
  }
  
  return new UploadError(type, message, details, recoverable);
};

export const isRetryableError = (errorType: UploadErrorType): boolean => {
  return ['network_error', 'server_error', 'verification_failed', 'email_update_failed', 'unknown'].includes(errorType);
};

export const getUploadErrorMessage = (error: UploadError): string => {
  return error.recoverable ? `${error.message} - Try again` : error.message;
};

export const showUploadErrorToast = (error: UploadError): void => {
  console.error(`Upload error (${error.type}):`, error.message, error.details);
  
  toast({
    title: error.message,
    description: error.details,
    variant: "destructive"
  });
};
